import { useRef, useState } from "react";
import Cropper, {
  type Area,
  type MediaSize,
  type Point,
  type Size,
} from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { App, Modal, Slider } from "antd";
import { useTranslation } from "react-i18next";
import BrandButton from "@/components/BrandButton";
import { cropImageToDataUrl, cropOutputSize } from "./cropImage";

const MAX_ZOOM = 3;
/** 圆形裁剪框固定边长。大图缩小、小图放大，都让一边落到这个尺寸。 */
const ROUND_CROP_SIZE: Size = { width: 120, height: 120 };

/** 最小缩放刚好铺满可视区。圆形允许大于 1，用来把小图放大到圆的直径。 */
function shrinkZoom(
  media: MediaSize,
  crop: Size,
  shape: ImageCropShape,
): number {
  if (!media.width || !media.height || !crop.width || !crop.height) return 1;
  const fit =
    shape === "round"
      ? Math.max(crop.width / media.width, crop.height / media.height)
      : Math.min(crop.width / media.width, crop.height / media.height);
  if (!Number.isFinite(fit) || fit <= 0) return 1;
  return shape === "round" ? fit : Math.min(1, fit);
}

export type ImageCropShape = "rect" | "round";

export interface ImageCropModalProps {
  open: boolean;
  src: string;
  /** 原图 MIME，决定导出格式。默认按 JPEG 导出。 */
  mime?: string;
  title?: string;
  /** 裁剪框宽高比，默认 1:1。 */
  aspect?: number;
  /** 裁剪框形状。圆形只影响预览，导出仍是矩形。 */
  cropShape?: ImageCropShape;
  /** 导出长边像素，默认 512。 */
  outputSize?: number;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
}

export function ImageCropModal({
  open,
  src,
  mime = "image/jpeg",
  title,
  aspect = 1,
  cropShape = "rect",
  outputSize = 512,
  onCancel,
  onConfirm,
}: ImageCropModalProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(MAX_ZOOM);
  const [ready, setReady] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const areaRef = useRef<Area | null>(null);
  const mediaRef = useRef<MediaSize | null>(null);
  const cropSizeRef = useRef<Size | null>(null);
  const seenSrc = useRef(src);
  const fittedSrc = useRef("");

  if (seenSrc.current !== src) {
    seenSrc.current = src;
    fittedSrc.current = "";
    mediaRef.current = null;
    cropSizeRef.current = null;
    areaRef.current = null;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setMaxZoom(MAX_ZOOM);
    setReady(false);
    setConfirming(false);
  }

  const syncMinZoom = () => {
    const media = mediaRef.current;
    const cropSize = cropSizeRef.current;
    if (!media || !cropSize) return;
    const nextMin = shrinkZoom(media, cropSize, cropShape);
    const nextMax = Math.max(MAX_ZOOM, nextMin * 3);
    const first = fittedSrc.current !== src;
    if (first) fittedSrc.current = src;
    setMinZoom(nextMin);
    setMaxZoom(nextMax);
    setZoom((current) => {
      const base = first ? 1 : current;
      return Math.min(nextMax, Math.max(nextMin, base));
    });
  };

  const confirm = async () => {
    const area = areaRef.current;
    if (!area || confirming) return;
    setConfirming(true);
    try {
      const size = cropOutputSize(aspect, outputSize);
      onConfirm(await cropImageToDataUrl(src, area, { mime, ...size }));
    } catch {
      message.error(t("common.cropFailed"));
      setConfirming(false);
    }
  };

  return (
    <Modal
      open={open}
      centered
      width={480}
      title={title ?? t("common.cropTitle")}
      footer={null}
      destroyOnHidden
      closable={!confirming}
      onCancel={confirming ? undefined : onCancel}
    >
      <div className="flex flex-col gap-4">
        <div className="relative h-80 overflow-hidden rounded-md bg-black">
          {src ? (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={0}
              aspect={aspect}
              cropShape={cropShape}
              cropSize={cropShape === "round" ? ROUND_CROP_SIZE : undefined}
              showGrid={false}
              minZoom={minZoom}
              maxZoom={maxZoom}
              restrictPosition
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onMediaLoaded={(media) => {
                mediaRef.current = media;
                if (cropShape === "round") cropSizeRef.current = ROUND_CROP_SIZE;
                syncMinZoom();
              }}
              onCropSizeChange={(cropSize) => {
                cropSizeRef.current = cropSize;
                syncMinZoom();
              }}
              onCropComplete={(_, pixels) => {
                areaRef.current = pixels;
                setReady(true);
              }}
            />
          ) : null}
        </div>
        <label className="flex items-center gap-3 text-sm text-secondary">
          <span className="shrink-0">{t("common.cropZoom")}</span>
          <Slider
            className="flex-1"
            min={minZoom}
            max={maxZoom}
            step={0.01}
            value={zoom}
            tooltip={{ formatter: null }}
            onChange={(value) => {
              if (typeof value === "number") setZoom(value);
            }}
          />
        </label>
        <div className="flex justify-end gap-2">
          <BrandButton
            variant="outline"
            type="button"
            disabled={confirming}
            onClick={onCancel}
          >
            {t("common.cancel")}
          </BrandButton>
          <BrandButton
            type="button"
            loading={confirming}
            disabled={!ready}
            onClick={() => void confirm()}
          >
            {t("common.confirm")}
          </BrandButton>
        </div>
      </div>
    </Modal>
  );
}

export { cropImageToDataUrl, cropOutputSize } from "./cropImage";
